import { useState, useRef, useEffect } from 'react';

import { calculateSlideshowDuration } from '../helpers';

/**
 * Slideshow Playback hook.
 *
 * @param {Object} options The options.
 * @param {Array} [options.slideshowSlides] The collection of slideshow photos or layouts
 * @param {Object} [options.slideshowSlide] The current active slideshow slide
 * @param {Number} [options.slideshowSlideIdx] The current active slideshow slide index
 * @param {Number} [options.nextSlideId] The ID of the next slide to be displayed
 * @param {Number} [options.slideDurationMs] The time each slide lasts (in ms)
 * @param {Number} [options.slideshowDuration] The time it takes for the slideshow to complete (in ms)
 * @param {Number} [options.intervalValue] The amount of time to wait before updating the elapsed time (in ms)
 * @param {Boolean} [options.restartOnEnd] Whether to stop at the last slide, or restart when the slideshow ends
 * @param {Boolean} [options.autoPlay] Whether to start playing after loading, or wait for user input to do so
 * @param {Boolean} [options.allowDeselect] Whether to allow deselecting (having no active slide)
 * @param {function} [options.onChangeSlide] Function to call once the time for the next slide to be displayed comes
 */
const useSlideshowPlayback = ({
  slideshowSlides,
  slideshowSlide: currentSlide,
  slideshowSlideIdx: currentSlideIdx,
  nextSlideId,
  slideDurationMs,
  slideshowDuration,
  intervalValue = 1000,
  restartOnEnd = true,
  autoPlay = true,
  allowDeselect = false,
  onChangeSlide,
}) => {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [skipFadeIn, setSkipFadein] = useState(!autoPlay);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [slideDuration, setSlideDuration] = useState(slideDurationMs);
  const [slideRemainingTime, setSlideRemainingTime] = useState(slideDurationMs);

  const counter = useRef(null);

  const onPlaybackStop = () => {
    setIsPlaying(false);
    clearInterval(counter.current);
    counter.current = null;
  };

  const onPlaybackStart = () => {
    // If we have no slides, reset everything and stop
    if ((slideshowSlides?.length ?? 0) === 0) {
      onPlaybackStop();
      return;
    }
    // If this is the case when we hit play, then we must focus on resetting the slides to 1st one.
    if (elapsedTime === slideshowDuration || !currentSlide) {
      setElapsedTime(0);
      const firstSlide = slideshowSlides[0];
      const { asset_image, photo, account_card } = firstSlide;

      if (currentSlide !== firstSlide) {
        setSlideDuration(slideDurationMs);
        setSlideRemainingTime(slideDurationMs);
        // history.replace(`${basePath}/${id}`);
        onChangeSlide({
          nextSlideId: asset_image?.id ?? photo?.id ?? account_card?.id,
        });
      }

      setIsPlaying(true);
      return;
    }

    counter.current = setInterval(() => {
      setElapsedTime(elapsed => elapsed + intervalValue);
      setSlideRemainingTime(remaining => remaining - intervalValue);
    }, intervalValue);
    setIsPlaying(true);
  };

  const onThumbnailClick = ({ slideId, slideIdx }) => {
    onPlaybackStop();
    // Ignore the changes to elapsed time and history if we click on the same slide
    if (
      !allowDeselect &&
      slideId === (currentSlide?.photo?.id ?? currentSlide?.account_card?.id)
    ) {
      return;
    }

    setSkipFadein(true);
    if (typeof slideIdx === 'undefined') {
      setElapsedTime(0);
    } else {
      setElapsedTime(
        calculateSlideshowDuration(slideshowSlides, slideDurationMs, slideIdx),
      );
    }

    onChangeSlide({ nextSlideId: slideId });
  };

  // Basically reset elapsedTime, slideDuration, and slideRemainingTime
  // to their defaults if we change the slideDurationMs. This is intended
  // to avoid having awkward situations where the user is playing around
  // with the delay slider all while the slideshow is playing.
  useEffect(() => {
    setElapsedTime(
      slideshowSlides && currentSlideIdx
        ? calculateSlideshowDuration(
            slideshowSlides,
            slideDurationMs,
            currentSlideIdx,
          )
        : 0,
    );
    setSlideDuration(slideDurationMs);
    setSlideRemainingTime(slideDurationMs);
  }, [slideDurationMs]);

  useEffect(() => {
    if (currentSlide) {
      clearInterval(counter.current);
      counter.current = null;
      setSlideDuration(slideDurationMs);
      setSlideRemainingTime(slideDurationMs);

      if (isPlaying) {
        setSkipFadein(false);
        onPlaybackStart();
      }
    }
  }, [currentSlide]);

  useEffect(() => {
    if (slideRemainingTime === 0 && isPlaying) {
      // Set the slide remaining time to a value other than 0
      // since having a 0 here will make it skip the other slide
      // before the useEffect above takes place and updates it
      setSlideRemainingTime(-1);

      // In the case where SlideshowSlides is 1, we won't change the slides.
      // Instead, we need to rely on resetting the current one.
      if (slideshowSlides.length === 1) {
        clearInterval(counter.current);
        counter.current = null;
        setSlideDuration(slideDurationMs);
        setSlideRemainingTime(slideDurationMs);

        setSkipFadein(false);
        onPlaybackStart();
      } else if (nextSlideId) {
        onChangeSlide({ nextSlideId });
      }
    }
  }, [slideRemainingTime, nextSlideId]);

  useEffect(() => {
    if (elapsedTime === slideshowDuration && !restartOnEnd) {
      onPlaybackStop();
    } else if (elapsedTime === slideshowDuration && restartOnEnd) {
      onPlaybackStart();
      // This should never happen, but safeguard against it just in case
    } else if (elapsedTime > slideshowDuration) {
      clearInterval(counter.current);
      counter.current = null;
      setElapsedTime(slideshowDuration);
    }
  }, [elapsedTime]);

  useEffect(() => {
    // Ignore this when the currentSlideIndex = 0
    // This is done to avoid stopping on reset, where
    // the index (0) would reset elapsedTime and possibly
    // break the interaction of the useEffect above. This,
    // however, will not be the case if the slideshow is not
    // playing.
    if (currentSlideIdx || !isPlaying) {
      setElapsedTime(
        calculateSlideshowDuration(
          slideshowSlides,
          slideDurationMs,
          currentSlideIdx,
        ),
      );
    }
  }, [slideshowSlides, currentSlideIdx]);

  return {
    isPlaying,
    elapsedTime,
    slideDuration,
    slideRemainingTime,
    slideshowDuration,
    skipFadeIn,
    onThumbnailClick,
    onPlaybackStart,
    onPlaybackStop,
  };
};

export default useSlideshowPlayback;
